'use client';

import { createContext, useCallback, useEffect, useState } from 'react';

import axios from 'axios';
import { UserAuthProps } from '@/types/User';
import { ResponseError } from '@/types/Error';
import { api } from '../services/api';
import { useRouter } from 'next/navigation';

type StatusProps =
  | 'signin'
  | 'new_account'
  | 'recover_account'
  | 'confirm_account'
  | 'account_successfully';

type AuthContextProps = {
  setRefererCookies: (referer: string) => void;
  signIn(
    username: string,
    password: string,
    errorAction: () => void,
  ): Promise<void>;
  singOut(): void;
  profile: UserAuthProps | null;
  isLoading: boolean;
  menuStatus: StatusProps;
  confirmAccountData: {
    id: string | null;
    expireDate: string | null;
  };
  handleChangeStatus: (status: StatusProps) => void;
  handleSetConfirmStatus: (data: { id: string; expireDate: string }) => void;
  resendConfirmation: (id: string, callBack: () => void) => Promise<void>;
  confirmationEmail: (
    id: string,
    code: string,
    callBack: () => void,
    callBackSuccess: () => void,
  ) => Promise<void>;
  handleUpdateProfileData: (
    data: UserAuthProps,
    expireDate?: string | null,
  ) => void;
  handleAccountDeleted: () => void;
  isAuthenticated: boolean;
};

type AuthProvidreProps = {
  children: React.ReactNode;
};

export const AuthContext = createContext({} as AuthContextProps);

export function AuthProvider({ children }: AuthProvidreProps) {
  const [mounted, setMounted] = useState(false);
  const [profile, setProfile] = useState<UserAuthProps | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [referer, setReferer] = useState<string | null>(null);

  const [menuStatus, setMenuStatus] = useState<StatusProps>('signin');
  const [confirmAccountData, setConfirmAccountData] = useState<{
    id: string | null;
    expireDate: string | null;
  }>({
    id: null,
    expireDate: null,
  });

  const { push } = useRouter();

  const isAuthenticated = !!profile;

  const handleChangeStatus = useCallback((status: StatusProps) => {
    setMenuStatus(status);
  }, []);

  const handleSetConfirmStatus = useCallback(
    (data: { id: string; expireDate: string }) => {
      setConfirmAccountData(data);
      handleChangeStatus('confirm_account');
    },
    [handleChangeStatus],
  );

  const handleUpdateProfileData = useCallback(
    (data: UserAuthProps, expireDate: string | null = null) => {
      setProfile(data);
      if (expireDate) {
        setConfirmAccountData({
          id: data.id,
          expireDate,
        });
      }
    },
    [],
  );

  const confirmationEmail = useCallback(
    async (
      id: string,
      code: string,
      callBack: () => void,
      callBackSuccess: () => void,
    ) => {
      try {
        const response = await api.post(
          `/customer-profile/confirmation-email/${id}`,
          {
            code,
          },
        );
        if (response.data === true) {
          setConfirmAccountData({ id: null, expireDate: null });
          callBackSuccess();
        }
      } catch (e) {
        const err = e as ResponseError;

        throw new Error(
          err.response && err.response.data
            ? err.response.data.message
            : 'Error na comunicação com o servidor',
        );
      } finally {
        callBack();
      }
    },
    [],
  );

  const resendConfirmation = useCallback(
    async (id: string, callBack: () => void) => {
      try {
        const response = await api.post(
          `/customer-profile/resend-confirmation`,
          {
            id,
          },
        );

        handleSetConfirmStatus(response.data);
      } catch (e) {
        const err = e as ResponseError;

        throw new Error(
          err.response && err.response.data
            ? err.response.data.message
            : 'Error na comunicação com o servidor',
        );
      } finally {
        callBack();
      }
    },
    [handleSetConfirmStatus],
  );

  const signIn = useCallback(
    async (email: string, password: string, callBack: () => void) => {
      try {
        const response = await api.post(`/customer-profile/auth`, {
          email,
          password,
        });

        if (response.data.user_data) {
          setProfile(response.data.user_data);
        } else if (response.data.expireDate) {
          handleSetConfirmStatus(response.data);
        }
      } catch (e) {
        const err = e as ResponseError;

        throw new Error(
          err.response && err.response.data
            ? err.response.data.message
            : 'Error na comunicação com o servidor',
        );
      } finally {
        callBack();
      }
    },
    [handleSetConfirmStatus],
  );

  const singOut = useCallback(async () => {
    try {
      await api.post('/customer-profile/logout');
    } catch {
      // do nothing
    }
    setProfile(null);
    push('/');
  }, [push]);

  const handleAccountDeleted = useCallback(() => {
    setProfile(null);
    push('/');
  }, [push]);

  const setRefererCookies = useCallback(
    (refe: string) => {
      if (!referer && refe) {
        api.defaults.headers.common['x-client-referer'] = refe;
        setReferer(refe);
      }
    },
    [referer],
  );

  const getClientIP = useCallback(async () => {
    try {
      const response = await axios.get('https://api.ipify.org?format=json');
      const { ip } = response.data;

      if (ip) api.defaults.headers.common['X-CLIENT-PUBLIC-IP'] = ip;
    } catch {
      // do nothing
    }
  }, []);

  const getProfileData = useCallback(async () => {
    try {
      const response = await api.get(`/customer-profile`);
      setProfile(response.data.user_data);
    } catch {
      setProfile(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setMounted(true);
    // 🔥 Só aceder localStorage DEPOIS de montar
    if (typeof window !== 'undefined') {
      // carregar dados do localStorage aqui
    }
  }, []);

  useEffect(() => {
    getClientIP();
  }, [getClientIP]);

  useEffect(() => {
    getProfileData();
  }, [getProfileData]);

  // 🔥 Durante SSR, retornar estado inicial neutro
  if (!mounted) {
    return (
      <AuthContext.Provider
        value={{
          setRefererCookies,
          signIn,
          singOut,
          profile: null,
          isLoading: true,
          isAuthenticated: false,
          menuStatus: 'signin',
          confirmAccountData: {
            id: null,
            expireDate: null,
          },
          handleChangeStatus,
          handleSetConfirmStatus,
          resendConfirmation,
          confirmationEmail,
          handleUpdateProfileData,
          handleAccountDeleted,
        }}
      >
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <AuthContext.Provider
      value={{
        setRefererCookies,
        signIn,
        singOut,
        profile,
        isLoading,
        isAuthenticated,
        menuStatus,
        confirmAccountData,
        handleChangeStatus,
        handleSetConfirmStatus,
        resendConfirmation,
        confirmationEmail,
        handleUpdateProfileData,
        handleAccountDeleted,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
